import {useEffect, useState} from 'react'

const SCRIPT_ID = 'google-maps-api-script'
let loadPromise: Promise<void> | null = null

type GoogleWindow = Window &
  typeof globalThis & {
    google?: {
      maps?: {
        places?: unknown
      }
    }
  }

const getGoogleWindow = () => window as GoogleWindow

const hasGooglePlacesLoaded = () =>
  typeof window !== 'undefined' && !!getGoogleWindow().google?.maps?.places

const loadGoogleMapsApi = (language = 'es') => {
  if (typeof window === 'undefined') return Promise.resolve()

  if (hasGooglePlacesLoaded()) {
    return Promise.resolve()
  }

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  if (!apiKey) {
    return Promise.reject(new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY environment variable'))
  }

  if (loadPromise) return loadPromise

  loadPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), {once: true})
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load Google Maps script')),
        {once: true},
      )
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.type = 'text/javascript'
    script.async = true
    script.defer = true
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=${language}`
    script.addEventListener('load', () => resolve(), {once: true})
    script.addEventListener('error', () => reject(new Error('Failed to load Google Maps script')), {
      once: true,
    })
    document.head.appendChild(script)
  })

  return loadPromise
}

interface UseLoadGoogleMapsOptions {
  language?: string
}

export const useLoadGoogleMaps = ({language = 'es'}: UseLoadGoogleMapsOptions = {}) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(() => hasGooglePlacesLoaded())
  const [loadError, setLoadError] = useState<Error | null>(null)

  useEffect(() => {
    let isMounted = true

    if (hasGooglePlacesLoaded()) {
      setIsLoaded(true)
      return
    }

    loadGoogleMapsApi(language)
      .then(() => {
        if (isMounted) {
          setIsLoaded(true)
          setLoadError(null)
        }
      })
      .catch((error) => {
        if (isMounted) {
          setLoadError(error instanceof Error ? error : new Error('Failed to load Google Maps script'))
        }
      })

    return () => {
      isMounted = false
    }
  }, [language])

  return {
    isLoaded,
    loadError,
  }
}
